/**
 * GEN-2915 (DEBT-05) — util CANÔNICO de documento (CNPJ/CPF/CEP) do frontend.
 *
 * Antes desta task a regra fiscal vivia espalhada: máscara de CNPJ e validação em
 * `pages/Companies/Company/util/cnpj.ts`, máscara de CPF em `pages/Persona/utils/cpf.ts`
 * (com DOIS formatadores divergentes no mesmo arquivo — um progressivo e um que assumia
 * 11 dígitos prontos), e a validação de documento dividida entre o algoritmo da casa e a
 * lib `cpf-cnpj-validator`.
 *
 * O que a duplicação custava, concretamente: dentro do MESMO wizard de criação de
 * empresas em lote, a etapa 1 validava com o algoritmo da casa (que aceita o CNPJ
 * alfanumérico da Receita) e a etapa de revisão validava com `cpf-cnpj-validator`, que
 * reduz o valor a dígitos antes de conferir e portanto REJEITA todo CNPJ alfanumérico.
 * A mesma empresa passava numa etapa e era recusada na seguinte.
 *
 * Regra de ouro deste arquivo: limpeza de CNPJ é `[^A-Za-z0-9]`, NUNCA `\D`. O CNPJ
 * alfanumérico tem letras nas 12 primeiras posições — reduzir a dígitos apaga documento
 * válido. CPF é numérico, e só ali `\D` é correto.
 *
 * Espelha `src/_base/util.ts` do taxly-backend (mesmo algoritmo de DV, mesmos pesos).
 * Mudança aqui pede a mudança espelhada lá.
 */

// ---------------------------------------------------------------------------
// Limpeza
// ---------------------------------------------------------------------------

/** Só os dígitos. Use para CPF/CEP — para CNPJ, use `cleanCnpj`. */
export const onlyDigits = (value: string): string => (value ? value.replace(/\D/g, "") : "");

/**
 * Limpa um CNPJ removendo máscara/pontuação/espaços, preservando letras e
 * convertendo para maiúsculas.
 *
 * Suporta tanto o CNPJ numérico legado quanto o novo CNPJ alfanumérico
 * (12 posições alfanuméricas + 2 dígitos verificadores numéricos).
 */
export const cleanCnpj = (cnpj: string): string => {
  if (!cnpj) return "";
  return cnpj.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
};

/**
 * Mantido com a assinatura antiga (removia apenas `-./`) porque é o que os
 * formulários de Pessoa Física gravam hoje; `cleanCpf` é o caminho novo.
 */
export const clearCpf = (cpf: string): string => (cpf ? cpf.replace(/[-./]/g, "") : "");

// ---------------------------------------------------------------------------
// Máscaras
// ---------------------------------------------------------------------------

/**
 * Aplica a máscara visual do CNPJ (XX.XXX.XXX/XXXX-XX) usando slices, de modo que
 * funcione para CNPJ alfanumérico sem remover letras. Valor parcial é formatado até
 * onde for possível (é a máscara de quem está digitando).
 */
export const formatCnpj = (cnpj: string): string => {
  if (!cnpj) return cnpj;
  const c = cleanCnpj(cnpj).slice(0, 14);
  if (c.length === 0) return "";
  if (c.length <= 2) return c;
  if (c.length <= 5) return `${c.slice(0, 2)}.${c.slice(2)}`;
  if (c.length <= 8) return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5)}`;
  if (c.length <= 12) return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8)}`;
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12, 14)}`;
};

/**
 * Máscara do CPF (000.000.000-00), progressiva pelo mesmo desenho de slices do CNPJ.
 *
 * Unifica os dois formatadores que existiam em `Persona/utils/cpf.ts`: um progressivo
 * (`maskCpf`) e um que exigia 11 dígitos já limpos (`formatCpf`) e devolvia a entrada
 * intacta em qualquer outro caso. Ficar com o progressivo é o comportamento correto nos
 * dois usos — digitação E exibição de valor já completo dão o mesmo resultado.
 */
export const formatCpf = (cpf: string): string => {
  if (!cpf) return cpf;
  const c = onlyDigits(cpf).slice(0, 11);
  if (c.length === 0) return "";
  if (c.length <= 3) return c;
  if (c.length <= 6) return `${c.slice(0, 3)}.${c.slice(3)}`;
  if (c.length <= 9) return `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6)}`;
  return `${c.slice(0, 3)}.${c.slice(3, 6)}.${c.slice(6, 9)}-${c.slice(9, 11)}`;
};

export const formatCep = (cep: string): string => {
  const truncated = onlyDigits(cep).slice(0, 8);
  return truncated.replace(/(\d{5})(\d{3})/, "$1-$2");
};

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------

const CNPJ_FIRST_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_SECOND_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Calcula um dígito verificador do CNPJ usando o algoritmo oficial alfanumérico:
 * cada caractere contribui com seu (código ASCII - 48), ponderado, somado mod 11.
 */
const cnpjCheckDigit = (base: string, weights: number[]): number => {
  const sum = base.split("").reduce((acc, char, i) => acc + (char.charCodeAt(0) - 48) * weights[i], 0);
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

/**
 * Valida um CNPJ (numérico legado ou novo alfanumérico) pelo algoritmo oficial de
 * dígito verificador da Receita Federal. As 12 primeiras posições podem ser letras
 * (A-Z) ou dígitos; os 2 dígitos verificadores devem ser numéricos.
 */
export const isValidCnpj = (cnpj: string): boolean => {
  const c = cleanCnpj(cnpj);

  if (c.length !== 14) return false;
  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(c)) return false;
  // Rejeita repetição trivial (ex.: todos os caracteres iguais)
  if (/^(.)\1{13}$/.test(c)) return false;

  const base = c.slice(0, 12);
  const dv1 = cnpjCheckDigit(base, CNPJ_FIRST_WEIGHTS);
  const dv2 = cnpjCheckDigit(base + dv1.toString(), CNPJ_SECOND_WEIGHTS);

  return c.slice(12) === `${dv1}${dv2}`;
};

/**
 * Valida um CPF pelo algoritmo de dígito verificador.
 *
 * Existe para tirar a dependência `cpf-cnpj-validator` do repo: manter uma lib só para
 * CPF, ao lado do algoritmo próprio de CNPJ, é o que deixava a validação de documento
 * com dois donos. Mesmas rejeições da lib (tamanho, repetição trivial, DV).
 *
 * Nasceu em `Persona/utils/cpf.ts` na GEN-3342 e a razão dela continua valendo: na Base
 * Cadastral a IDENTIDADE da pessoa é o CPF (é por ele que o módulo consolida "todas as
 * empresas em que fulano aparece"). Documento inventado ali não erra uma linha só — vira
 * uma pessoa nova que nunca casa com a real, e a consolidação passa a mentir. Por isso a
 * repetição trivial é barrada ANTES da conta: `111.111.111-11` fecha os dois dígitos.
 */
export const isValidCpf = (cpf: string): boolean => {
  const c = onlyDigits(cpf);

  if (c.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(c)) return false;

  const digit = (upTo: number): number => {
    let sum = 0;
    for (let i = 0; i < upTo; i += 1) {
      sum += Number(c[i]) * (upTo + 1 - i);
    }
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  return digit(9) === Number(c[9]) && digit(10) === Number(c[10]);
};

// ---------------------------------------------------------------------------
// Genérico por tipo de entidade
// ---------------------------------------------------------------------------

/** Formata o documento conforme o tipo da entidade (empresa → CNPJ, pessoa → CPF). */
export const formatDocument = (document: string | null, entity_type: string | number | undefined): string => {
  if (!document) {
    return "";
  }

  if (entity_type === "company" || entity_type === 0 || entity_type === 1) {
    return formatCnpj(document);
  }

  if (entity_type === "persona" || entity_type === 3) {
    return formatCpf(document);
  }

  return document;
};
